import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button, Card, useToastStore } from '@invoiceflow/ui';
import { usePortalApi } from '@invoiceflow/portal-core';
import { useQueryClient } from '@tanstack/react-query';
import { firebaseSignOut } from '../services/firebase';
import { useAuthStore } from '../stores/useAuthStore';

interface Props {
  open: boolean;
  onClose: () => void;
}

const CONFIRM_WORD = 'DELETE';

/**
 * Deletion is immediate and irreversible, so the consequences are stated
 * before it happens rather than after — including, importantly, what is NOT
 * deleted. A customer who thinks this erases their invoices would be misled,
 * and one who fears it does would never use it.
 */
export const DeleteAccountDialog: React.FC<Props> = ({ open, onClose }) => {
  const [confirmation, setConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { addToast } = useToastStore();
  const logout = useAuthStore(state => state.logout);
  const api = usePortalApi();
  const queryClient = useQueryClient();

  if (!open) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.account.remove();
      // The Firebase session is already revoked server-side; this clears the
      // local SDK state so the app does not briefly think it is signed in.
      await firebaseSignOut();
      logout();
      queryClient.clear();
      addToast('Your account has been deleted.', 'success');
    } catch {
      addToast('We could not delete your account. Please try again.', 'error');
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-overlay/40 backdrop-blur-sm">
      <Card className="max-w-md w-full !p-8 !rounded-3xl border-none shadow-2xl space-y-5">
        <div className="flex items-start justify-between">
          <div className="h-12 w-12 rounded-2xl bg-danger-subtle text-danger flex items-center justify-center">
            <AlertTriangle size={22} />
          </div>
          <button
            onClick={onClose}
            className="p-2 text-content-muted hover:text-content-secondary transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-black text-content">Delete your account?</h2>
          <p className="text-sm text-content-muted font-medium">
            This removes your sign-in and your access to every company&apos;s portal. It cannot be
            undone.
          </p>
        </div>

        <div className="bg-surface-muted rounded-2xl p-4 space-y-1.5">
          <p className="text-xs font-black text-content-muted uppercase tracking-widest">
            What stays
          </p>
          <p className="text-sm text-content-secondary font-medium">
            Your invoices, payments and contact details remain with PT Alia Primavera as part of
            their business records. Deleting your account does not change what you owe or what you
            have paid.
          </p>
        </div>

        <div className="bg-surface-muted rounded-2xl p-4 space-y-1.5">
          <p className="text-xs font-black text-content-muted uppercase tracking-widest">
            Coming back
          </p>
          <p className="text-sm text-content-secondary font-medium">
            If we send you another invoice, you can sign in again and set up access from scratch.
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="confirm-delete"
            className="block text-sm font-bold text-content-secondary"
          >
            Type {CONFIRM_WORD} to confirm
          </label>
          <input
            id="confirm-delete"
            value={confirmation}
            onChange={e => setConfirmation(e.target.value)}
            autoComplete="off"
            className="block w-full px-3 py-2 border border-line rounded-xl text-sm focus:ring-danger focus:border-danger"
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={onClose} variant="secondary" className="flex-1" disabled={isDeleting}>
            Keep my account
          </Button>
          <Button
            onClick={handleDelete}
            disabled={confirmation !== CONFIRM_WORD || isDeleting}
            className="flex-1 !bg-danger hover:!bg-danger"
          >
            {isDeleting ? 'Deleting...' : 'Delete account'}
          </Button>
        </div>
      </Card>
    </div>
  );
};
